/**
 * Where a recording's bytes go.
 *
 * This is the seam that keeps ONE recorder serving both the admin test bench
 * and a real interview. The recorder knows nothing about destinations; it hands
 * every Blob to a sink in arrival order. Swapping the sink is the entire
 * difference between "download this to my machine" and "stream this to S3".
 *
 * Two implementations exist:
 *
 *   LocalBlobSink    — admin bench. Stays in the browser. Never uploads.
 *   S3MultipartSink  — real interview. Uploads directly to S3. (multipart-uploader)
 *
 * ORDER IS LOAD-BEARING. A MediaRecorder timeslice stream is one continuous
 * byte stream: the first Blob carries the EBML header, every later Blob is bare
 * continuation bytes. Concatenating them out of order, or dropping one from the
 * middle, corrupts everything after it. A sink may upload concurrently, but it
 * must preserve byte order in the final object.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Admin-bench sink: holds the recording in browser memory and nowhere else.
 *
 * It deliberately accumulates every Blob, which is exactly what the interview
 * path must NOT do. That is safe and correct here: a bench recording is short,
 * operator-initiated, and its whole purpose is to be previewed and downloaded
 * locally. It never touches S3, the upload APIs, or the database.
 *
 * Callers MUST call `dispose()` when discarding, or the object URL leaks.
 */
export class LocalBlobSink {
    constructor(mimeType) {
        this.mimeType = mimeType;
        this.chunks = [];
        this.objectUrl = null;
        this._bytes = 0;
    }
    get bytesAccepted() {
        return this._bytes;
    }
    accept(blob) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!blob || blob.size === 0)
                return;
            this.chunks.push(blob);
            this._bytes += blob.size;
        });
    }
    finalize() {
        return __awaiter(this, void 0, void 0, function* () {
            /* nothing to flush — the blobs are already here */
        });
    }
    abort() {
        return __awaiter(this, void 0, void 0, function* () {
            this.dispose();
        });
    }
    /** The concatenated recording. Byte-identical to what S3 multipart would
     *  assemble from the same Blobs, which is what makes the bench a faithful
     *  rehearsal of the real pipeline. */
    toBlob() {
        return new Blob(this.chunks, { type: this.mimeType });
    }
    /** A URL for `<video src>` / a download anchor. Cached so repeated reads do
     *  not leak; revoked by `dispose()`. */
    toObjectUrl() {
        if (this.objectUrl === null) {
            this.objectUrl = URL.createObjectURL(this.toBlob());
        }
        return this.objectUrl;
    }
    /** Release the object URL and drop the buffered blobs. Safe to call twice. */
    dispose() {
        if (this.objectUrl !== null) {
            try {
                URL.revokeObjectURL(this.objectUrl);
            }
            catch (_a) {
                /* revoking twice, or in a torn-down document, is not worth reporting */
            }
            this.objectUrl = null;
        }
        this.chunks = [];
        this._bytes = 0;
    }
}
